import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { GlobalsStore } from '../../../stores/globals.state';

@Component({
  selector: 'app-value-comparison-visual',
  standalone: true,
  imports: [ProgressBarModule, TooltipModule],
  templateUrl: './value-comparison-visual.component.html',
  styleUrl: './value-comparison-visual.component.scss',
})
export class ValueComparisonVisualComponent implements OnInit, OnChanges {
  readonly globalsStore = inject(GlobalsStore);
  @Input() max!: number | null;
  @Input() min!: number | null;
  @Input() value!: number | null;
  @Input() comp!: number | null;
  @Input() indicatorId!: string;

  normalizedValue!: number; // The normalized value (percentage)
  normalizedComp!: number | null; // The normalized comparison value (percentage)

  /**
   * Lifecycle hook that is called when any data-bound property of a directive changes.
   * This method detects input property changes and can perform operations as part of
   * the Angular change detection cycle.
   *
   * @param {SimpleChanges} changes - An object representing the current and previous values
   *                                   of changed input properties.
   * @return {void} - This method does not return any value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.normalizeValues();
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * Initializes or normalizes component-related values and performs any necessary setup.
   * Calls the `normalizeValues` method to prepare the component for use.
   *
   * @return {void} Does not return a value.
   */
  ngOnInit(): void {
    this.normalizeValues();
  }

  /**
   * Normalizes the value, competitor's value (if present), and calculates the range
   * as percentages relative to the difference between the minimum and maximum values.
   * It ensures no division by zero occurs if the maximum and minimum values are equal.
   *
   * @return {void} This method does not return a value; it updates the object's properties directly.
   */
  normalizeValues(): void {
    if (this.max === null || this.min === null || this.value === null) return;
    const range = this.max - this.min;

    // Prevent division by zero if min and max are the same
    if (range === 0) {
      this.normalizedValue = 100;
      this.normalizedComp = 100;
      return;
    }

    // Convert to percentages relative to range
    this.normalizedValue = ((this.value - this.min) / range) * 100;
    this.normalizedComp = this.comp
      ? ((this.comp - this.min) / range) * 100
      : null;
  }

  /**
   * Formats the given value using the formatting logic defined in the globals store for the specific indicator.
   *
   * @param {number | null} value - The value to be formatted. Can be a number or null.
   * @return {string} The formatted value as a string.
   */
  getValueFormatted(value: number | null): string {
    return this.globalsStore.getFormattedValue(value, this.indicatorId);
  }
}
